import { type MatchFunction, type MatchResult, match } from 'path-to-regexp';

export type PathMatch<
    Params extends { [K in keyof Params]?: string } = { [key: string]: string | undefined },
> = MatchResult<Params>;

const matchPathCache = new Map<string, MatchFunction<{ [key: string]: string }>>();

/**
 * Normalize the basepath
 */
export function normalizePath(basepath = ''): string {
    basepath = basepath[basepath.length - 1] === '/' ? basepath.slice(0, -1) : basepath;
    basepath = basepath[0] === '/' ? basepath : `/${basepath}`;
    return basepath;
}

/*
 * Runs the first matching route for given pathname,
 * like a functional <ReactRouter.Switch>
 */
export function switcher<Result>(
    pathname: string,
    routes: Array<{
        path: string;
        exact?: boolean;
        onMatch: (match: PathMatch) => Result;
    }>,
    options: { prefix?: string } = {}
): Result | undefined {
    let result: Result | undefined;

    let { prefix = '' } = options;
    if (prefix === '/') {
        prefix = '';
    }

    if (!pathname.startsWith(prefix)) {
        return;
    }

    pathname = pathname.slice(prefix.length);
    routes.some((routeDef) => {
        const cacheKey = `${!!routeDef.exact}${routeDef.path}`;
        const routePath = routeDef.path;
        const pathMatch =
            matchPathCache.get(cacheKey) ||
            match(routePath, {
                decode: decodeURIComponent,
                start: true,
                end: !!routeDef.exact,
                sensitive: false,
                strict: false,
            });
        matchPathCache.set(cacheKey, pathMatch);

        const matchResult = pathMatch(pathname);
        if (matchResult) {
            result = routeDef.onMatch({
                ...matchResult,
                path: `${prefix}${matchResult.path}`,
            });
            return true;
        }
    });

    return result;
}
